import React, { useState } from 'react';
import {
  FormControl,
  FormLabel,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { UseFormMethods } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { logger } from 'src/lib/logger';
import { ImageUploader } from 'src/components/image-uploader';
import { useDropzone } from 'react-dropzone';
import { CacheKey } from 'src/constants/cache-key';
import { SvgAddImage } from 'src/components/icons';
import { SIDE_MENU } from './types';
import { useUploadCoverMutation } from '../../studio/hooks/use-studio-mutation';
import { Article } from '../types';

interface BasicSectionProps {
  contentID: string;
  section: string;
  register: UseFormMethods['register'];
  showCoverUpload?: boolean;
}

function BasicSection({
  contentID,
  section,
  register,
  showCoverUpload = true,
}: BasicSectionProps) {
  const [articleCoverImgURL, setArticleImgCoverURL] = useState('');

  const queryClient = useQueryClient();
  const articleCache = queryClient.getQueryData<Article>([
    CacheKey.article,
    contentID,
  ]);
  const {
    mutateAsync: uploadCover,
    isLoading: isUploadingProjectCover,
  } = useUploadCoverMutation(contentID);

  const coverImgURL = articleCache?.coverImgURL || articleCoverImgURL;

  const handleUploadCover = async (files: File[]) => {
    if (files[0]) {
      const file = files[0];
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await uploadCover({ data: formData });

        if (response.code === 1000) {
          setArticleImgCoverURL(response.imgURL);
        }
      } catch (err) {
        logger.error(err);
      }
    }
  };

  const dropzone = useDropzone({ onDrop: handleUploadCover });

  return (
    <Stack
      display={section === SIDE_MENU.BASIC ? 'flex' : 'none'}
      spacing={5}
      direction="column"
      width="100%"
    >
      {/* Project Cover */}
      {showCoverUpload && (
        <FormControl id="project_cover">
          <FormLabel color="gray.700">
            <Stack direction="row" spacing={2}>
              <Text as="b">Cover Image</Text>
              <Text fontSize="xs">Required</Text>
            </Stack>
          </FormLabel>
          <ImageUploader
            dropzone={dropzone}
            uploading={isUploadingProjectCover}
            src={coverImgURL}
            bg="white"
            height="150px"
            border="1px solid"
            borderColor="gray.200"
            color="gray.500"
          >
            <SvgAddImage fontSize="24px" />
            <Text as="b" ml={1} fontSize="sm">
              Upload an Image
            </Text>
          </ImageUploader>
          <Text color="gray.500" fontSize="xs">
            *304x298, PNG, JPG format with less than 5MB
          </Text>
        </FormControl>
      )}

      {/* Project Description */}
      <FormControl id="description">
        <FormLabel color="gray.700">
          <Text as="b">Project Description</Text>
        </FormLabel>
        <Textarea
          name="description"
          placeholder="Max. 200 characters"
          ref={register({ maxLength: 200 })}
        />
      </FormControl>
    </Stack>
  );
}

export default BasicSection;
